import { Link } from 'react-router-dom';
import { useCurrency, type Currency } from '../lib/currency';
import { useTheme } from '../lib/theme';
import { useWallet } from '../lib/wallet';
import { cents } from '../lib/format';

const NAV = [
  { label: 'Home', href: '/home' },
  { label: 'Explore', href: '/explore' },
  { label: 'Channels', href: '/channels' },
  { label: 'Drops', href: '/drops' },
];

/**
 * Provny-branded top bar. Follows the global light/dark preference (the sun/moon
 * toggle here drives it) so the nav always matches the body beneath it. Publish
 * CTA is cyan on both.
 */
export function Header() {
  const { isDark: dark } = useTheme();

  return (
    <header
      className={`sticky top-0 z-20 border-b backdrop-blur ${
        dark
          ? 'border-white/10 bg-provny-ink/85 text-provny-white'
          : 'border-provny-border bg-provny-white/85 text-provny-black'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-8">
          <Link to="/" aria-label="Provny — home" className="font-heading text-3xl font-black lowercase tracking-tight">
            provny
          </Link>
          <nav className="hidden gap-6 text-sm font-medium md:flex">
            {NAV.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={
                  dark
                    ? 'text-white/60 transition-colors hover:text-white'
                    : 'text-provny-muted transition-colors hover:text-provny-black'
                }
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <SupportTally dark={dark} />
          <ThemeToggle dark={dark} />
          <CurrencyToggle dark={dark} />
          <button className="rounded-xl bg-provny-cyber px-4 py-2 text-sm font-semibold text-provny-ink transition hover:bg-provny-cyber/90">
            Publish
          </button>
        </div>
      </div>
    </header>
  );
}

/**
 * Running support tally — closes the loop on micro-unlocks. Hidden until the
 * reader has backed at least one creator. Reads from the wallet context.
 */
function SupportTally({ dark }: { dark: boolean }) {
  const { supportedCreators, spentCents } = useWallet();
  if (supportedCreators === 0) return null;

  const border = dark ? 'border-white/20' : 'border-provny-border';

  return (
    <span
      className={`hidden items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold sm:inline-flex ${border}`}
      title={`You've backed ${supportedCreators} creator${supportedCreators === 1 ? '' : 's'}`}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#D4A015" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
      </svg>
      Backed {supportedCreators} · {cents(spentCents)}
    </span>
  );
}

/** Light/dark switcher. Shows the icon for the mode you'd switch *to*. */
function ThemeToggle({ dark }: { dark: boolean }) {
  const { toggle } = useTheme();
  const ring = dark ? 'border-white/20 hover:border-white/40' : 'border-provny-border hover:border-provny-muted';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border transition ${ring}`}
    >
      {dark ? (
        // sun
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // moon
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

/** SOL / USD price-display switcher, mirroring MemeGenes. Adapts to header theme. */
function CurrencyToggle({ dark }: { dark: boolean }) {
  const { currency, setCurrency } = useCurrency();
  const border = dark ? 'border-white/20' : 'border-provny-border';

  return (
    <div className={`flex items-center gap-0.5 rounded-lg border p-0.5 text-xs font-semibold ${border}`}>
      {(['USD', 'SOL'] as Currency[]).map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => setCurrency(c)}
          aria-pressed={currency === c}
          className={`rounded-md px-2 py-1 transition ${
            currency === c ? 'bg-provny-cyber text-provny-ink' : 'opacity-60 hover:opacity-100'
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}
